import { useHistory, useRouteMatch } from '@react-router';
import { useCallback } from 'react';

import { withWindow } from '@shared/utils/withWindow';
import { CATALOG_PATH, CURATIONS_PATH, BABY_SHOP_PATH, BRANDS_PATH } from '../Catalog.constants';

import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';

export type UseCatalogRoutingReturn = ReturnType<typeof useCatalogRouting>;

// TODO: Rewrite to match routerHelper
export const useCatalogRouting = () => {
  const history = useHistory();

  const { catalogRoutes, isBabyStorefront } = useCatalogRoutePaths();

  const adminRouteMatch = useRouteMatch<{ eventHandle: string }>('/:eventHandle/edit/registry');
  const adminSubRouteMatch = useRouteMatch<{ eventHandle: string; subRoute: string }>('/:eventHandle/edit/registry/:subRoute');
  const eventHandle = adminRouteMatch?.params.eventHandle;

  const isAdminView = !!adminRouteMatch;

  const isShopRootRoute = useRouteMatch(catalogRoutes.catalogIndex.paths);

  const isCurationsRootRoute = useRouteMatch(catalogRoutes.curationsIndex.paths);

  const isCurationSearchResultRoute = useRouteMatch(catalogRoutes.curationDetails.paths);

  const getContextualPath = useCallback(
    (path: string) => {
      return eventHandle ? `/${eventHandle}/edit/registry${path}` : path;
    },
    [eventHandle],
  );

  const getPublicPathFromContextualUrl = useCallback(
    (url: string) => {
      return eventHandle ? url.replace(`/${eventHandle}/edit/registry`, '') : url;
    },
    [eventHandle],
  );

  const goToPath = useCallback(
    (pathname: string, action: 'push' | 'replace' = 'push', queryParams?: string, force: boolean = false) => {
      if (pathname !== history.location.pathname || queryParams || force) {
        history[action]({
          pathname,
          search: queryParams,
        });
      }
    },
    [history],
  );

  const getToCategoryPath = useCallback(
    (publicUrlPath: string) => {
      return getContextualPath(publicUrlPath);
    },
    [getContextualPath],
  );

  const goToCategory = useCallback(
    (publicUrlPath: Readonly<string>, action?: 'push' | 'replace', queryParams?: string) => {
      window.history.replaceState({}, document.title);
      goToPath(getToCategoryPath(publicUrlPath), action, queryParams);
    },
    [goToPath, getToCategoryPath],
  );

  const goToCatalogItem = useCallback(
    (catalogItemId: Readonly<string>, action?: 'push' | 'replace', queryParams?: string) => {
      goToPath(getContextualPath(`${CATALOG_PATH}/item/${catalogItemId}`), action, queryParams);
    },
    [goToPath, getContextualPath],
  );

  const getCatalogItemPath = useCallback(
    (catalogItemId: Readonly<string>) => {
      return getContextualPath(`${CATALOG_PATH}/item/${catalogItemId}`);
    },
    [getContextualPath],
  );

  const getRootCatalogPath = useCallback(() => {
    return getContextualPath(isBabyStorefront ? BABY_SHOP_PATH : CATALOG_PATH);
  }, [isBabyStorefront, getContextualPath]);

  const goToRootCatalog = useCallback(
    (queryParams?: string) => {
      goToPath(getRootCatalogPath(), 'push', queryParams, true);
    },
    [goToPath, getRootCatalogPath],
  );

  const getToRootCurationsPath = useCallback(() => {
    return getContextualPath(`${isBabyStorefront ? BABY_SHOP_PATH : CATALOG_PATH}${CURATIONS_PATH}`);
  }, [isBabyStorefront, getContextualPath]);

  const getToRootBrandsPath = useCallback(() => {
    return getContextualPath(`${isBabyStorefront ? BABY_SHOP_PATH : CATALOG_PATH}${BRANDS_PATH}`);
  }, [isBabyStorefront, getContextualPath]);

  const goToRootCurations = useCallback(() => {
    goToPath(getToRootCurationsPath());
  }, [goToPath, getToRootCurationsPath]);

  const goToRootBrands = useCallback(() => {
    goToPath(getToRootBrandsPath());
  }, [goToPath, getToRootBrandsPath]);

  const getToCurationDetailsPath = useCallback(
    (publicUrlPath: string) => {
      return getContextualPath(publicUrlPath);
    },
    [getContextualPath],
  );

  const goToCurationDetails = useCallback(
    (publicUrlPath: string, queryParams?: string) => {
      goToPath(getToCurationDetailsPath(publicUrlPath), 'push', queryParams);
      withWindow(global => global.scrollTo({ top: 0 }));
    },
    [goToPath, getToCurationDetailsPath],
  );

  const goToBrandPage = useCallback(
    (publicUrlPath: string) => {
      goToPath(getContextualPath(publicUrlPath));
    },
    [goToPath, getContextualPath],
  );

  return {
    history,
    getRootCatalogPath,
    getPublicPathFromContextualUrl,
    getToCategoryPath,
    getToCurationDetailsPath,
    getToRootCurationsPath,
    getCatalogItemPath,
    isAdminView,
    isCurationsRootRoute: !!isCurationsRootRoute?.isExact,
    isShopRootRoute: !!isShopRootRoute?.isExact,
    isCurationSearchResultRoute: !!isCurationSearchResultRoute?.isExact,
    adminRegistrySubRoute: adminSubRouteMatch?.params.subRoute,

    goToCategory,
    goToCatalogItem,
    goToCurationDetails,
    goToRootCatalog,
    goToRootBrands,
    goToRootCurations,
    goToBrandPage,
  } as const;
};
